import { LanguagesEnum } from '@/api/enums';
import {
  PhishingLanguagesEnum,
  phishingLanguageDisplayLabel,
} from '@/api/enums/phishing-languages.enum';
import { AppButton, FlexContainer } from '@/ui';
import { getPhishingTranslationLanguageOptions } from '@/utils/helpers';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const TARGET_LANGUAGES_ID = 'phishing-translation-target-languages';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
  padding: var(--medium-padding);
  border: 1px solid var(--gray-border-lighter);
  border-radius: var(--xsmall-border-radius);
`;

const SourceLabel = styled.div`
  font-size: var(--small-font-size);
  color: var(--black-main);
  letter-spacing: 0.28px;

  span {
    font-weight: 600;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--semi-xsmall-padding);
`;

const FieldLabel = styled.label`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--black-main);
`;

export type PhishingTranslationSettingsProps = {
  sourceLanguage: PhishingLanguagesEnum;
  linkedLanguages: PhishingLanguagesEnum[];
  initialTargetLanguages?: LanguagesEnum[];
  isStarting?: boolean;
  onStart: (targetLanguages: LanguagesEnum[]) => void;
  onCancel: () => void;
};

export const PhishingTranslationSettings: React.FC<
  PhishingTranslationSettingsProps
> = ({
  sourceLanguage,
  linkedLanguages,
  initialTargetLanguages,
  isStarting = false,
  onStart,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [targetLanguages, setTargetLanguages] = useState<LanguagesEnum[]>(
    initialTargetLanguages ?? [],
  );

  const options = useMemo(
    () => getPhishingTranslationLanguageOptions(linkedLanguages),
    [linkedLanguages],
  );

  return (
    <Card>
      <SourceLabel>
        {t('phishing.translation.settings.sourceLabel')}{' '}
        <span>{phishingLanguageDisplayLabel[sourceLanguage]}</span>
      </SourceLabel>

      <FieldGroup>
        <FieldLabel htmlFor={TARGET_LANGUAGES_ID}>
          {t('phishing.translation.settings.targetLabel')}
        </FieldLabel>
        <MultiSelect
          inputId={TARGET_LANGUAGES_ID}
          value={targetLanguages}
          options={options}
          onChange={(e: MultiSelectChangeEvent) => setTargetLanguages(e.value)}
          placeholder={t('phishing.translation.settings.targetPlaceholder')}
          display="chip"
          filter
          disabled={isStarting}
          className="w-full"
        />
      </FieldGroup>

      <FlexContainer gap={14} justify="flex-end">
        <AppButton
          label={t('button.cancel')}
          type="outlined"
          isDisabled={isStarting}
          onClick={onCancel}
        />
        <AppButton
          label={t('phishing.translation.settings.startButton')}
          isDisabled={isStarting || !targetLanguages.length}
          onClick={() => onStart(targetLanguages)}
        />
      </FlexContainer>
    </Card>
  );
};
